import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  AdminAssessmentFamily,
  AdminAssessmentListItem,
  AdminDocumentListItem,
  AdminTenantListItem,
  AdminTenantMember,
} from '@libs/types';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { ListPagination } from '@components';
import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import {
  ArrowLeftIcon,
  BracketsCurlyIcon,
  BuildingsIcon,
  CaretDownIcon,
  CaretRightIcon,
  ClipboardTextIcon,
  CurrencyDollarIcon,
  DownloadSimpleIcon,
  FileIcon,
  FilesIcon,
  FolderIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  ToggleLeftIcon,
  UserPlusIcon,
  UsersIcon,
} from '@phosphor-icons/react';

import {
  useAdminTenant,
  useAdminTenantAssessments,
  useAdminTenantDocuments,
  useAdminTenantMembers,
  useDownloadAdminDocument,
  useRevokeTenantUser,
} from '../api/admin-api';
import { AddUserModal } from '../components/AddUserModal';
import { formatBytes, formatDate } from '../components/adminFormat';
import { AdminStatusChip } from '../components/AdminStatusChip';
import { AdminTable } from '../components/AdminTable';
import { adminButtonSx, adminColors, CONFIDENCE_META } from '../components/adminTheme';
import { AssessmentDocumentsDialog } from '../components/AssessmentDocumentsDialog';
import { ComplianceProfileTab } from '../components/ComplianceProfileTab';
import { EditableUser, EditUserModal } from '../components/EditUserModal';
import { MetadataDialog } from '../components/MetadataDialog';
import { OrganizationDetailsTab } from '../components/OrganizationDetailsTab';
import { SetPasswordModal } from '../components/SetPasswordModal';
import { TenantFeaturesTab } from '../components/TenantFeaturesTab';
import { TenantUsagePanel } from '../components/usage/TenantUsagePanel';

const PAGE_SIZE = 10;

const memberName = (m: AdminTenantMember) =>
  `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.username;

const memberInitials = (m: AdminTenantMember) => {
  const base = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.username || m.email;
  return base.slice(0, 2).toUpperCase();
};

const cellBorder = (border: string) => ({ borderBottom: `1px solid ${border}` });

/* ── Users tab ───────────────────────────────────────────────────── */
const USERS_PAGE_SIZE = 10;

const UsersTab: FC<{ tenant: AdminTenantListItem }> = observer(({ tenant }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const { data: members, isLoading } = useAdminTenantMembers(tenant.id);
  const revoke = useRevokeTenantUser(tenant.id);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<EditableUser | null>(null);
  const [pwdUser, setPwdUser] = useState<{ id: string; label: string } | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const columns = [
    { key: 'user', label: t('app.admin.detail.usersTab.table.user'), width: '24%' },
    { key: 'email', label: t('app.admin.detail.usersTab.table.email'), width: '26%' },
    { key: 'username', label: t('app.admin.detail.usersTab.table.username'), width: '16%' },
    { key: 'status', label: t('app.admin.detail.usersTab.table.status'), width: '10%' },
    { key: 'actions', label: t('app.admin.detail.usersTab.table.actions'), align: 'right' as const, width: '24%' },
  ];

  const filtered = useMemo(() => {
    const list = members ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((m) =>
      `${m.firstName ?? ''} ${m.lastName ?? ''} ${m.email} ${m.username}`.toLowerCase().includes(q),
    );
  }, [members, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / USERS_PAGE_SIZE));
  // Keep the current page within range as the filter narrows the result set.
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);
  const paged = filtered.slice((page - 1) * USERS_PAGE_SIZE, page * USERS_PAGE_SIZE);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.75, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.125,
            backgroundColor: theme.palette.grey[200],
            border: `1px solid ${c.border}`,
            borderRadius: 2.5,
            px: 1.5,
            py: 1,
            flex: 1,
            maxWidth: 360,
          }}
        >
          <MagnifyingGlassIcon size={16} color={c.faint} />
          <InputBase
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder={t('app.admin.detail.usersTab.search')}
            sx={{ flex: 1, fontSize: 13, color: c.ink }}
          />
        </Box>
        <Button
          onClick={() => setAddOpen(true)}
          startIcon={<UserPlusIcon size={16} />}
          sx={{ ...adminButtonSx(theme).solid, px: 2, py: 1, borderRadius: 2.5 }}
        >
          {t('app.admin.detail.usersTab.addUser')}
        </Button>
      </Box>
      <AdminTable
        columns={columns}
        minWidth={760}
        isLoading={isLoading}
        isEmpty={!filtered.length}
        emptyLabel={query ? t('app.admin.detail.usersTab.noMatches') : t('app.admin.detail.usersTab.empty')}
        footer={
          filtered.length > USERS_PAGE_SIZE ? (
            <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} sx={{ mt: 0 }} />
          ) : undefined
        }
      >
        {paged.map((m) => (
          <TableRow key={m.id}>
            <TableCell sx={cellBorder(c.border)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 600, background: 'linear-gradient(135deg,#14B8A6,#0063B3)' }}>
                  {memberInitials(m)}
                </Avatar>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: c.ink }}>{memberName(m)}</Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ ...cellBorder(c.border), fontSize: 12.5, color: c.sub }}>{m.email}</TableCell>
            <TableCell sx={{ ...cellBorder(c.border), fontFamily: 'monospace', fontSize: 12, color: c.sub }}>{m.username}</TableCell>
            <TableCell sx={cellBorder(c.border)}>
              <AdminStatusChip status={m.enabled ? 'active' : 'suspended'} />
            </TableCell>
            <TableCell align="right" sx={cellBorder(c.border)}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
                <IconButton
                  onClick={() => setEditUser({ id: m.id, firstName: m.firstName, lastName: m.lastName, email: m.email, enabled: m.enabled })}
                  title={t('app.admin.editUser.title')}
                  sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}
                >
                  <PencilSimpleIcon size={15} />
                </IconButton>
                <IconButton
                  onClick={() => setPwdUser({ id: m.id, label: memberName(m) })}
                  title={t('app.admin.setPassword.title')}
                  sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: c.violetBg, color: c.violet }}
                >
                  <KeyIcon size={15} />
                </IconButton>
                <Button
                  onClick={() => revoke.mutate(m.id)}
                  disabled={revoke.isPending}
                  sx={{ textTransform: 'none', fontSize: 12, color: c.error, backgroundColor: '#FFF1F0', border: '1px solid #FFCCC7', px: 1.5, py: 0.625, borderRadius: 2 }}
                >
                  {t('app.admin.detail.usersTab.revoke')}
                </Button>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </AdminTable>
      <AddUserModal open={addOpen} onClose={() => setAddOpen(false)} tenantId={tenant.id} tenantName={tenant.name} />
      {editUser && (
        <EditUserModal open onClose={() => setEditUser(null)} user={editUser} />
      )}
      {pwdUser && (
        <SetPasswordModal open onClose={() => setPwdUser(null)} userId={pwdUser.id} userLabel={pwdUser.label} />
      )}
    </Box>
  );
});

/* ── Assessments tab ─────────────────────────────────────────────── */
const LatestBadge: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  return (
    <Box sx={{ fontSize: 9.5, fontWeight: 600, color: c.azure, backgroundColor: '#EFF8FF', borderRadius: 1, px: 0.75, py: 0.125 }}>
      {t('app.admin.detail.assessTab.latest')}
    </Box>
  );
});

const AssessmentsTab: FC<{ tenantId: string }> = observer(({ tenantId }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [docTarget, setDocTarget] = useState<AdminAssessmentListItem | null>(null);
  const { data, isLoading, isFetching } = useAdminTenantAssessments(tenantId, { page, limit: PAGE_SIZE });
  const families = data?.rows ?? [];
  const meta = data?.meta;

  const toggle = (rootId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });

  const columns = [
    { key: 'title', label: t('app.admin.detail.assessTab.table.id'), width: '36%' },
    { key: 'version', label: t('app.admin.detail.assessTab.table.version'), width: '14%' },
    { key: 'status', label: t('app.admin.detail.assessTab.table.status'), width: '16%' },
    { key: 'docs', label: t('app.admin.detail.assessTab.table.documents'), align: 'center' as const, width: '10%' },
    { key: 'completed', label: t('app.admin.detail.assessTab.table.completed'), width: '12%' },
    { key: 'updated', label: t('app.admin.detail.assessTab.table.updated'), width: '12%' },
  ];

  const renderFamily = (fam: AdminAssessmentFamily) => {
    const hasChildren = fam.versions.length > 1;
    const isOpen = expanded.has(fam.rootId);
    const latest = fam.latest;

    // Single-version families open their documents directly; multi-version
    // families toggle the accordion, and each version row opens its own docs.
    const onRowClick = () => (hasChildren ? toggle(fam.rootId) : setDocTarget(latest));

    const rows = [
      <TableRow
        key={fam.rootId}
        hover
        onClick={onRowClick}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell sx={cellBorder(c.border)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 18, display: 'flex', flexShrink: 0, color: c.faint }}>
              {hasChildren &&
                (isOpen ? <CaretDownIcon size={14} weight="bold" /> : <CaretRightIcon size={14} weight="bold" />)}
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: c.ink }}>{fam.title}</Typography>
            {latest.isLatest && <LatestBadge />}
          </Box>
        </TableCell>
        <TableCell sx={{ ...cellBorder(c.border), fontSize: 12.5, color: c.sub }}>
          {hasChildren ? t('app.admin.detail.assessTab.versionsCount', { n: fam.versions.length }) : `v${latest.version}`}
        </TableCell>
        <TableCell sx={cellBorder(c.border)}>
          <AdminStatusChip status={latest.status} />
        </TableCell>
        <TableCell align="center" sx={{ ...cellBorder(c.border), fontSize: 13, color: c.ink }}>{latest.documentCount}</TableCell>
        <TableCell sx={{ ...cellBorder(c.border), fontSize: 12.5, color: c.sub }}>{formatDate(latest.completedAt)}</TableCell>
        <TableCell sx={{ ...cellBorder(c.border), fontSize: 12.5, color: c.sub }}>{formatDate(latest.updatedAt)}</TableCell>
      </TableRow>,
    ];

    if (hasChildren && isOpen) {
      for (const v of fam.versions) {
        rows.push(
          <TableRow
            key={v.id}
            hover
            onClick={() => setDocTarget(v)}
            sx={{ cursor: 'pointer', backgroundColor: theme.palette.action.hover }}
          >
            <TableCell sx={cellBorder(c.border)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3.5 }}>
                <FilesIcon size={14} color={c.faint} style={{ flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, color: c.sub }}>
                  {t('app.admin.detail.assessTab.viewDocuments')}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ ...cellBorder(c.border), fontSize: 12.5, color: c.sub }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>v{v.version}</span>
                {v.isLatest && <LatestBadge />}
              </Box>
            </TableCell>
            <TableCell sx={cellBorder(c.border)}>
              <AdminStatusChip status={v.status} />
            </TableCell>
            <TableCell align="center" sx={{ ...cellBorder(c.border), fontSize: 13, color: c.ink }}>{v.documentCount}</TableCell>
            <TableCell sx={{ ...cellBorder(c.border), fontSize: 12.5, color: c.sub }}>{formatDate(v.completedAt)}</TableCell>
            <TableCell sx={{ ...cellBorder(c.border), fontSize: 12.5, color: c.sub }}>{formatDate(v.updatedAt)}</TableCell>
          </TableRow>,
        );
      }
    }

    return rows;
  };

  return (
    <>
      <AdminTable
        columns={columns}
        minWidth={820}
        isLoading={isLoading}
        isEmpty={!families.length}
        emptyLabel={t('app.admin.detail.assessTab.empty')}
        footer={
          meta && meta.totalCount > 0 ? (
            <ListPagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              onPageChange={setPage}
              isLoading={isFetching}
              sx={{ mt: 0 }}
            />
          ) : undefined
        }
      >
        {families.flatMap(renderFamily)}
      </AdminTable>
      <AssessmentDocumentsDialog
        open={!!docTarget}
        onClose={() => setDocTarget(null)}
        tenantId={tenantId}
        assessment={docTarget}
      />
    </>
  );
});

/* ── Documents tab ───────────────────────────────────────────────── */
const ConfidenceChip: FC<{ confidence: AdminDocumentListItem['confidence'] }> = observer(({ confidence }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  if (!confidence) return <Typography sx={{ fontSize: 12, color: c.faint }}>—</Typography>;
  const meta = CONFIDENCE_META[confidence];
  const color = meta.tone === 'green' ? c.green : meta.tone === 'warn' ? c.warn : c.error;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, backgroundColor: `${color}18`, borderRadius: 1.5, px: 1, py: 0.375, width: 'fit-content' }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color }}>{t(meta.labelKey)}</Typography>
    </Box>
  );
});

const DocumentsTab: FC<{ tenantId: string }> = observer(({ tenantId }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const [page, setPage] = useState(1);
  const [metaDoc, setMetaDoc] = useState<AdminDocumentListItem | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { data, isLoading, isFetching } = useAdminTenantDocuments(tenantId, { page, limit: PAGE_SIZE });
  const rows = data?.rows ?? [];
  const meta = data?.meta;

  const downloadDoc = useDownloadAdminDocument();

  const handleDownloadDoc = (doc: AdminDocumentListItem) => {
    if (downloadingId) return;
    setDownloadingId(doc.id);
    downloadDoc.mutate(
      { tenantId, documentId: doc.id, fileName: doc.fileName },
      { onSettled: () => setDownloadingId(null) },
    );
  };

  const columns = [
    { key: 'file', label: t('app.admin.detail.docsTab.table.file'), width: '30%' },
    { key: 'source', label: t('app.admin.detail.docsTab.table.source'), width: '20%' },
    { key: 'classification', label: t('app.admin.detail.docsTab.table.classification'), width: '16%' },
    { key: 'confidence', label: t('app.admin.detail.docsTab.table.confidence'), width: '14%' },
    { key: 'size', label: t('app.admin.detail.docsTab.table.size'), width: '8%' },
    { key: 'actions', label: t('app.admin.detail.docsTab.table.actions'), align: 'right' as const, width: '12%' },
  ];

  return (
    <>
      <AdminTable
        columns={columns}
        minWidth={880}
        isLoading={isLoading}
        isEmpty={!rows.length}
        emptyLabel={t('app.admin.detail.docsTab.empty')}
        footer={
          meta && meta.totalCount > 0 ? (
            <ListPagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              onPageChange={setPage}
              isLoading={isFetching}
              sx={{ mt: 0 }}
            />
          ) : undefined
        }
      >
        {rows.map((d) => (
          <TableRow key={d.id}>
            <TableCell sx={cellBorder(c.border)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125, minWidth: 0 }}>
                <FileIcon size={17} color={d.classificationStatus === 'failed' ? c.error : c.sub} style={{ flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.fileName}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ ...cellBorder(c.border), fontSize: 12, color: c.sub }}>{d.source}</TableCell>
            <TableCell sx={cellBorder(c.border)}>
              {d.classificationStatus ? (
                <AdminStatusChip status={d.classificationStatus} />
              ) : (
                <Typography sx={{ fontSize: 12, color: c.faint }}>—</Typography>
              )}
            </TableCell>
            <TableCell sx={cellBorder(c.border)}>
              <ConfidenceChip confidence={d.confidence} />
            </TableCell>
            <TableCell sx={{ ...cellBorder(c.border), fontSize: 12, color: c.sub }}>{formatBytes(d.fileSize)}</TableCell>
            <TableCell align="right" sx={cellBorder(c.border)}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={() => handleDownloadDoc(d)}
                  disabled={downloadingId !== null}
                  title={t('app.admin.detail.docsTab.download')}
                  sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: theme.palette.grey[200] }}
                >
                  {downloadingId === d.id ? (
                    <CircularProgress size={14} sx={{ color: c.sub }} />
                  ) : (
                    <DownloadSimpleIcon size={15} color={c.sub} />
                  )}
                </IconButton>
                <IconButton
                  onClick={() => setMetaDoc(d)}
                  title={t('app.admin.detail.docsTab.inspect')}
                  sx={{ width: 30, height: 30, borderRadius: 2, backgroundColor: theme.palette.grey[200] }}
                >
                  <BracketsCurlyIcon size={15} color={c.violet} />
                </IconButton>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </AdminTable>
      <MetadataDialog open={!!metaDoc} onClose={() => setMetaDoc(null)} doc={metaDoc} />
    </>
  );
});

/* ── Page ────────────────────────────────────────────────────────── */
type TabKey =
  | 'details'
  | 'compliance'
  | 'users'
  | 'assessments'
  | 'documents'
  | 'usage'
  | 'features';

export const AdminTenantDetailPage: FC = observer(() => {
  const theme = useTheme();
  const { isRtl } = useDirection();
  const c = adminColors(theme);
  const { t } = useTrans();
  const navigate = useNavigate();
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const { data: tenant, isLoading } = useAdminTenant(tenantId);
  // Seeded from `?tab=` so the cost leaderboard can link straight to a
  // tenant's Usage tab. Read once on mount: the tab is local state after
  // that, matching how the other tabs on this page already behave.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(
    (searchParams.get('tab') as TabKey | null) ?? 'details',
  );

  const tabs: { key: TabKey; label: string; icon: typeof BuildingsIcon }[] = [
    { key: 'details', label: t('app.admin.detail.tabs.orgDetails'), icon: BuildingsIcon },
    { key: 'compliance', label: t('app.admin.detail.tabs.compliance'), icon: FolderIcon },
    { key: 'users', label: t('app.admin.detail.tabs.users'), icon: UsersIcon },
    { key: 'assessments', label: t('app.admin.detail.tabs.assessments'), icon: ClipboardTextIcon },
    { key: 'documents', label: t('app.admin.detail.tabs.documents'), icon: FilesIcon },
    { key: 'usage', label: t('app.admin.detail.tabs.usage'), icon: CurrencyDollarIcon },
    { key: 'features', label: t('app.admin.detail.tabs.features'), icon: ToggleLeftIcon },
  ];

  const name = tenant?.displayName || tenant?.name || '';
  const avatarInitials = name.split(' ').slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase();

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 3.5, py: 2.5 }}>
      <Button
        onClick={() => navigate(routes.adminTenants())}
        startIcon={<ArrowLeftIcon size={15} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
        sx={{ ...adminButtonSx(theme).ghost, fontSize: 12.5, mb: 2, px: 1, py: 0.5, ml: -1, borderRadius: 2 }}
      >
        {t('app.admin.detail.back')}
      </Button>

      {/* Org header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 3.25,
            background: 'linear-gradient(135deg,#14B8A6,#0063B3)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {avatarInitials}
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.375, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: c.ink }}>
              {name || (isLoading ? '…' : tenantId)}
            </Typography>
            {tenant && <AdminStatusChip status={tenant.enabled === false ? 'suspended' : 'active'} />}
          </Box>
          {tenant && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.625, fontSize: 12.5, color: c.sub, flexWrap: 'wrap' }}>
              <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>{tenant.name}</Typography>
              {tenant.sector && (<><span>·</span><Typography component="span" sx={{ fontSize: 12.5 }}>{tenant.sector}</Typography></>)}
              {tenant.memberCount != null && (<><span>·</span><Typography component="span" sx={{ fontSize: 12.5 }}>{t('app.admin.detail.membersCount', { n: tenant.memberCount })}</Typography></>)}
              {tenant.assessmentCount != null && (<><span>·</span><Typography component="span" sx={{ fontSize: 12.5 }}>{t('app.admin.detail.assessmentsCount', { n: tenant.assessmentCount })}</Typography></>)}
            </Box>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => setTab(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          borderBottom: `1px solid ${c.border}`,
          mb: 2.75,
          minHeight: 0,
          '& .MuiTab-root': { textTransform: 'none', minHeight: 0, py: 1.25, fontSize: 13, color: c.sub },
          '& .Mui-selected': { color: `${c.ink} !important` },
          '& .MuiTabs-indicator': { backgroundColor: c.ink },
        }}
      >
        {tabs.map((tb) => {
          const Icon = tb.icon;
          return (
            <Tab
              key={tb.key}
              value={tb.key}
              iconPosition="start"
              icon={<Icon size={16} />}
              label={tb.label}
              sx={{ gap: 0.75 }}
            />
          );
        })}
      </Tabs>

      {tenant && tab === 'details' && <OrganizationDetailsTab tenant={tenant} />}
      {tab === 'compliance' && <ComplianceProfileTab tenantId={tenantId} />}
      {tenant && tab === 'users' && <UsersTab tenant={tenant} />}
      {tab === 'assessments' && <AssessmentsTab tenantId={tenantId} />}
      {tab === 'documents' && <DocumentsTab tenantId={tenantId} />}
      {tab === 'usage' && <TenantUsagePanel tenantId={tenantId} />}
      {tab === 'features' && <TenantFeaturesTab tenantId={tenantId} />}
    </Box>
  );
});
